// src/App.tsx
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Router from './Router';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, Toast } from 'antd-mobile';
import Loader from './components/common/Loader';
import { ensureBridgeReady } from './utils/nativeBridge';
import { fetchAndApplyLocale } from './i18n/nativeLocale';
import { fetchAndApplyTheme } from './theming/nativeTheme';
import { getAntdLocale } from './i18n/antdLocale';


function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  // Gate the first render until the bridge is ready AND locale + theme are applied,
  // so the UI paints already-translated and already-themed (no flash). Falls back
  // to defaults on the hard timeout or any bridge failure.
  const [ready, setReady] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        setReady(true);
      }
    };
    const timer = setTimeout(finish, 2000);
    (async () => {
      try {
        await ensureBridgeReady();
        await Promise.allSettled([fetchAndApplyLocale(), fetchAndApplyTheme(dispatch)]);
      } catch {
        /* bridge unavailable / timed out — render with defaults */
      } finally {
        clearTimeout(timer);
        finish();
      }
    })();
    return () => clearTimeout(timer);
  }, [dispatch]);

  // Track "offline" state
  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Show a loader until the bridge is ready + locale/theme applied (or the timeout).
  if (!ready) {
    return <Loader />;
  }

  // If you want a "full screen" approach when offline:
  if (isOffline){
    return (
      Toast.show({
        content: 'Check your internet connection and retry again.',
      })
    );
  }

  // Otherwise, the normal app. ConfigProvider lives here (not at the root) so it
  // reads the active i18n language: App subscribes via useTranslation and re-renders
  // on languageChanged, so antd-mobile components get the right locale once boot
  // resolves it.
  return (
    <ConfigProvider locale={getAntdLocale(i18n.language)}>
      <Router />
    </ConfigProvider>
  );
}

export default App;
